// Local PDF + marks store (IndexedDB). The web analogue of iOS LecturePdfStore.
//
// A lecture run carries only the EXTRACTED TEXT in start_inputs (StartInputs is
// string-only), but the marking UI needs the ORIGINAL PDF to render. So at start
// time we stash the picked PDF blob here under a `ref` and put that ref in
// start_inputs as `local_pdf_ref`; the marking renderer loads the blob back by
// ref. We also persist in-progress marks per ref so a long session survives a
// reload before the user hits Generate. (Browser-local; cross-device sync later.)

// The DB name stays "edupause-lecture" even though it now also backs the
// standalone Study library (renaming would orphan in-progress lecture marks).
const DB_NAME = "edupause-lecture";
const STORE_PDF = "pdfs";
const STORE_MARKS = "marks";
const STORE_META = "pdf_meta"; // v2: enumerable per-PDF metadata for the Study library
const STORE_UNDERSTAND = "understanding"; // v3: cached one-time document understanding per PDF
const VERSION = 3;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    // Guarded creates: v1 users keep their existing pdfs/marks; only the new
    // pdf_meta store is added on the v1→v2 upgrade.
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_PDF)) db.createObjectStore(STORE_PDF);
      if (!db.objectStoreNames.contains(STORE_MARKS)) db.createObjectStore(STORE_MARKS);
      if (!db.objectStoreNames.contains(STORE_META)) db.createObjectStore(STORE_META);
      if (!db.objectStoreNames.contains(STORE_UNDERSTAND)) db.createObjectStore(STORE_UNDERSTAND);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function run<T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest,
): Promise<T> {
  return openDB().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result as T);
        req.onerror = () => reject(req.error);
        t.oncomplete = () => db.close();
      }),
  );
}

export async function savePdf(blob: Blob): Promise<string> {
  const ref =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `ref-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
  // Store the bytes as an ArrayBuffer, NOT a Blob/File. Safari/WebKit's
  // IndexedDB intermittently refuses to persist Blobs — it throws
  // "Error preparing Blob/File data to be stored in object store" (seen on
  // iPad in the Mark & Study flow). ArrayBuffers persist reliably everywhere;
  // loadPdf() rebuilds a Blob on read.
  const buf = await blob.arrayBuffer();
  await run(STORE_PDF, "readwrite", (s) => s.put(buf, ref));
  return ref;
}

/** Store a blob under a CALLER-provided ref (vs `savePdf`'s random ref). Used by
 *  the server-backed Study flow to cache a PDF under its known server docId so
 *  the marking workspace renders instantly without re-downloading. Same
 *  ArrayBuffer-not-Blob persistence detail as `savePdf` (see its note). */
export async function savePdfAs(ref: string, blob: Blob): Promise<void> {
  const buf = await blob.arrayBuffer();
  await run(STORE_PDF, "readwrite", (s) => s.put(buf, ref));
}

export async function loadPdf(ref: string): Promise<Blob | undefined> {
  const v = await run<ArrayBuffer | Blob | undefined>(
    STORE_PDF,
    "readonly",
    (s) => s.get(ref),
  );
  if (v == null) return undefined;
  // New entries are ArrayBuffers; legacy v1 entries persisted as Blobs (on
  // non-Safari browsers) are returned as-is for backward compatibility.
  return v instanceof Blob ? v : new Blob([v], { type: "application/pdf" });
}

export async function saveMarks<T>(ref: string, data: T): Promise<void> {
  await run(STORE_MARKS, "readwrite", (s) => s.put(data, ref));
}

export function loadMarks<T>(ref: string): Promise<T | undefined> {
  return run<T | undefined>(STORE_MARKS, "readonly", (s) => s.get(ref));
}

// ── One-time document understanding cache (v3) ───────────────────────────────
// The marking workspace extracts the PDF text once, asks the backend to
// understand it, and caches the compact bullets here keyed by PDF ref so the
// LLM call fires only once per document (survives reloads).

export interface DocUnderstanding {
  summary: string;
  topics: string[];
  learnings: string[];
  /** Pre-rendered compact bullet string passed as `context` on lookups. */
  contextText: string;
  /** Coverage/truncation notice to re-surface on a cache hit (large doc / scanned). */
  note?: string;
}

export async function saveUnderstanding(ref: string, data: DocUnderstanding): Promise<void> {
  await run(STORE_UNDERSTAND, "readwrite", (s) => s.put(data, ref));
}

export function loadUnderstanding(ref: string): Promise<DocUnderstanding | undefined> {
  return run<DocUnderstanding | undefined>(STORE_UNDERSTAND, "readonly", (s) => s.get(ref));
}

// ── Study-library metadata (v2) ──────────────────────────────────────────────
// Only the standalone Study flow writes meta, so lecture runs started from the
// Create gallery never appear in the Study library (by design).

export interface PdfMeta {
  ref: string;
  name: string;
  createdAt: number;
  lastOpenedAt?: number;
  markCount?: number;
}

export async function savePdfMeta(meta: PdfMeta): Promise<void> {
  await run(STORE_META, "readwrite", (s) => s.put(meta, meta.ref));
}

export function loadPdfMeta(ref: string): Promise<PdfMeta | undefined> {
  return run<PdfMeta | undefined>(STORE_META, "readonly", (s) => s.get(ref));
}

/** Merge a partial update into an existing meta record (no-op if none exists). */
export async function patchPdfMeta(ref: string, patch: Partial<PdfMeta>): Promise<void> {
  const cur = await loadPdfMeta(ref);
  if (!cur) return;
  await savePdfMeta({ ...cur, ...patch, ref: cur.ref });
}

export async function listPdfs(): Promise<PdfMeta[]> {
  const all = await run<PdfMeta[]>(STORE_META, "readonly", (s) => s.getAll());
  return (all ?? []).sort(
    (a, b) => (b.lastOpenedAt ?? b.createdAt) - (a.lastOpenedAt ?? a.createdAt),
  );
}

/** Remove a PDF entirely — blob, marks, and meta — in one transaction. */
export function deletePdfEntry(ref: string): Promise<void> {
  return openDB().then(
    (db) =>
      new Promise<void>((resolve, reject) => {
        const t = db.transaction(
          [STORE_PDF, STORE_MARKS, STORE_META, STORE_UNDERSTAND],
          "readwrite",
        );
        t.objectStore(STORE_PDF).delete(ref);
        t.objectStore(STORE_MARKS).delete(ref);
        t.objectStore(STORE_META).delete(ref);
        t.objectStore(STORE_UNDERSTAND).delete(ref);
        t.oncomplete = () => {
          db.close();
          resolve();
        };
        t.onerror = () => reject(t.error);
      }),
  );
}
